<template>
    <div :class="hideWrapper ? '' : 'c-preview-wrapper'">
        <img
            class="img-preview img-thumbnail"
            :class="{ 'img-fit': fit }"
            :src="source"
            :alt="alt"
            :loading="lazy ? 'lazy' : undefined"
            @error="onErrorHandler"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watchEffect } from 'vue';
import { buildCloudFrontUrl } from '@/utils/pathUtils';

const props = withDefaults(
    defineProps<{
        src: File | unknown;
        alt?: string;
        thumbnail?: boolean;
        hideWrapper?: boolean;
        width?: number;
        maxHeight?: string;
        lazy?: boolean;
        // Fills a width x maxHeight box instead of keeping the image's own height
        fit?: 'contain' | 'cover';
    }>(),
    { alt: 'file preview', maxHeight: '300px' }
);

const emit = defineEmits<{
    (event: 'preview-failed'): void;
}>();

const source = ref('');

const previewWidth = computed(() => {
    const width = props.thumbnail ? 50 : props.width;
    return `${width}px`;
});

watchEffect(() => {
    if (!props.src) return;

    if (typeof props.src == 'string') {
        source.value = props.src.startsWith('/Content') ? buildCloudFrontUrl(props.src) : props.src;
        return;
    }

    source.value = URL.createObjectURL(props.src as File);
});

onUnmounted(() => {
    //Revoke the url to avoid memory leaks.
    URL.revokeObjectURL(source.value);
});

const onErrorHandler = () => {
    emit('preview-failed');
};
</script>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.c-preview-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid get-color('gray-200');
    border-radius: 0.25rem;
    width: v-bind(previewWidth);
    min-width: v-bind(previewWidth);
    height: 100% !important;
    max-height: 100% !important;
    .img-preview.img-thumbnail {
        padding: 0;
        border: unset;
        max-height: v-bind(maxHeight);

        &.img-fit {
            width: 100%;
            height: v-bind(maxHeight);
            object-fit: v-bind(fit);
        }
    }
}
</style>
